import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { ATO_LOGBOOK, type FaqGroup } from "../pages/faq-items"
import { ATO_RECORDS_SOURCE } from "../pages/trust-facts"
import { DATA_COUNTRY_ZH, englishDateToZh, STORAGE_SENTENCE_ZH } from "./dates"

/**
 * Chinese FAQ (D-WEB-LANG-SCOPE). Same groups, order and question count as `FAQ_GROUPS`
 * (`zh-pages.test.tsx` checks it); source links reuse the English constants' URLs.
 * Terms: docs/design/landing/copy-zh.md. Logbook-only "App" menu names follow the iOS zh strings.
 */
export const FAQ_REVIEWED_ZH = englishDateToZh("28 October 2026")
export const FAQ_CHECKED_ZH = englishDateToZh("28 Oct 2026")

export const FAQ_GROUPS_ZH: FaqGroup[] = [
  {
    id: "records",
    title: "记录与 ATO",
    items: [
      {
        question: "我的记录应该保存多久？",
        answer:
          "ATO 要求大多数经营记录保存 5 年。你可以随时导出。付费套餐失效后，我们最多再保留你的数据 3 个月，并在数据从账户中移除之前多次提醒你导出。",
        sources: [{ label: "ATO，保存经营记录概览", href: ATO_RECORDS_SOURCE.href }],
      },
      {
        question: "这个应用得到 ATO 认可了吗？",
        answer:
          "没有。我们不声称得到 ATO 认可。我们按 ATO 在 logbook 法页面上列出的内容来设计行车日志，并为每条规则附上 ATO 页面的链接。",
        sources: [{ label: "ATO 对 logbook 法的说明", href: ATO_LOGBOOK.href }],
      },
    ],
  },
  {
    id: "privacy",
    title: "隐私与数据",
    items: [
      {
        question: "我的数据存放在哪里？",
        answer: `${STORAGE_SENTENCE_ZH}少数位于澳大利亚境外的服务会为我们处理部分数据，安全与隐私页面列出了这些服务。`,
      },
      {
        question: "我可以导出或删除我的数据吗？",
        answer: "可以。行程、收据和发票可导出成 CSV 或 PDF。你可以在“设置”里或在网页上删除账户。",
      },
      {
        question: "怎么导出我的数据？",
        answer:
          "在网站上登录，打开你的账户，选择“导出我的全部数据”，你会得到一个装着全部记录的 zip 文件。删除任何内容之前，请先导出。",
      },
      {
        question: "怎么删除我的账户？",
        answer:
          "在网站上的账户里删除。账户会在 30 天的缓冲期之后才被移除，期间你可以恢复；具体步骤见“删除账户”页面（英文）。如果你登录不了，请给我们发邮件。",
      },
    ],
  },
  {
    id: "app",
    title: "手机应用",
    items: [
      {
        question: "没有网络也能用吗？",
        answer: "能。行程和收据先存在手机上，联网后再同步。",
      },
      {
        question: "支持哪些手机？",
        answer: "iPhone 和 Android。网站用来核对、开发票和出报告。",
      },
      {
        question: "我的登录验证码没收到，或者已经过期，怎么办？",
        answer:
          "我们会用邮件发给你一个 6 位验证码，10 分钟内有效。请先查看垃圾邮件箱，并确认邮箱地址输入无误，然后在登录页面重新申请验证码。试了几次仍收不到，请给我们发邮件。",
      },
      {
        question: "怎么把应用日志发给你们？",
        answer:
          "在 iPhone 应用里依次打开“设置”、“诊断”、“分享日志”，再把文件附在邮件里。文件会先去掉坐标、邮箱地址和令牌。",
      },
    ],
  },
  {
    id: "billing",
    title: "付费",
    items: [
      {
        question: "试用要绑定银行卡吗？",
        answer: `不用。${TRIAL_DAYS} 天免费试用无需绑定银行卡。`,
      },
      {
        question: "在哪里管理付费和支付方式？",
        answer:
          "只在网站上，也就是你的账户里。iPhone 和 Android 应用不收款，所以在 Apple 或 Google 的设置里没有什么需要管理的。",
      },
    ],
  },
]

/** Page text of the FAQ hero and footer card. */
export const FAQ_PAGE_ZH = {
  title: "常见问题",
  description:
    "关于 ATO 记录保存、隐私、手机应用和付费的解答，每条回答都附来源和最近复核日期，适合澳大利亚个体经营者。",
  stats: [
    { value: "5 年", label: "ATO 记录规定" },
    { value: DATA_COUNTRY_ZH, label: "数据存放地" },
    { value: `${TRIAL_DAYS} 天`, label: "免费试用" },
  ],
  lastReviewed: (date: string) => `最近复核 ${date}`,
  jump: "跳到某个主题",
  questions: (n: number) => `${n} 个问题`,
  stuck: "还是没解决？",
  stuckBody: "给客服发邮件，会有真人回复；也可以使用",
  contactForm: "联系表单",
  contactSupport: "联系客服",
  ctaTitle: "想试试吗？",
  ctaDescription: `免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`,
  ctaLabel: "开始免费试用",
  keyFacts: "要点",
}
